import {
  EnhancedTableHeader,
  EnhancedTableHeaderType
} from '@components/table/widgets/shared-with-old';
import { getGenericLine } from '@utils/helper-utils';

import { GenericLines } from 'src/enums';

export const certificatesHeaders = (
  currencySymbol: string,
  lineCode: string,
  options?: { openCertificateInNewTab?: boolean }
): Record<string, EnhancedTableHeader> => {
  const openCertificateInNewTab = options?.openCertificateInNewTab ?? false;
  const isGroupMedical =
    getGenericLine(lineCode) === GenericLines.MEDICAL_GROUP;
  const isFleetLine = [4, 51].includes(Number(lineCode));
  const isAllRisk = Number(lineCode) === 51;
  const isMarine = getGenericLine(lineCode) === GenericLines.MARINE;

  if (isGroupMedical) {
    return {
      certificateNumber: {
        name: 'certificateNumber',
        title: 'Certificate Number',
        type: EnhancedTableHeaderType.Link,
        openUrlInNewTab: openCertificateInNewTab,
        urlTemplate: '/production/policies/{certificateID}'
      },
      principal: {
        name: 'principal',
        title: 'Principal',
        type: EnhancedTableHeaderType.Text
      },
      numberOfAdherent: {
        name: 'numberOfAdherent',
        title: 'Number of Adherent',
        type: EnhancedTableHeaderType.Text,
        width: '50px'
      },
      class: {
        name: 'class',
        title: 'Class',
        type: EnhancedTableHeaderType.Text
      },
      level: {
        name: 'level',
        title: 'Level',
        type: EnhancedTableHeaderType.Text
      },
      coNSSF: {
        name: 'coNSSF',
        title: 'Co-NSSF',
        type: EnhancedTableHeaderType.Text
      },
      sumInsured: {
        name: 'sumInsured',
        title: 'Sum Insured',
        type: EnhancedTableHeaderType.RoundedCurrency,
        currency: currencySymbol
      },
      netPremium: {
        name: 'netPremium',
        title: 'Net Premium',
        type: EnhancedTableHeaderType.RoundedCurrency,
        currency: currencySymbol
      },
      charges: {
        name: 'charges',
        title: 'Charges',
        type: EnhancedTableHeaderType.RoundedCurrency,
        currency: currencySymbol
      },
      tpaFees: {
        name: 'tpaFees',
        title: 'TPA Fees',
        type: EnhancedTableHeaderType.RoundedCurrency,
        currency: currencySymbol
      },
      grossPremium: {
        name: 'grossPremium',
        title: 'Gross Premium',
        type: EnhancedTableHeaderType.RoundedCurrency,
        currency: currencySymbol
      },
      proportionalStamp: {
        name: 'proportionalStamp',
        title: 'Prop Stamp',
        type: EnhancedTableHeaderType.RoundedCurrency,
        currency: currencySymbol
      },
      municipalityTax: {
        name: 'municipalityTax',
        title: 'Mun Tax',
        type: EnhancedTableHeaderType.RoundedCurrency,
        currency: currencySymbol
      },
      totalPremium: {
        name: 'totalPremium',
        title: 'Total Premium',
        type: EnhancedTableHeaderType.RoundedCurrency,
        currency: currencySymbol
      }
    };
  }

  if (isMarine) {
    return {
      certificateNumber: {
        name: 'certificateNumber',
        title: 'Certificate Number',
        type: EnhancedTableHeaderType.Link,
        openUrlInNewTab: false,
        urlTemplate: '/production/policies/{policyId}'
      },
      subline: {
        name: 'subline',
        title: 'Subline',
        type: EnhancedTableHeaderType.Text
      },
      plan: {
        name: 'plan',
        title: 'Plan',
        type: EnhancedTableHeaderType.Text
      },
      matter: {
        name: 'matter',
        title: 'Matter',
        type: EnhancedTableHeaderType.Text
      },
      vessalName: {
        name: 'vessalName',
        title: 'Vessel Name',
        type: EnhancedTableHeaderType.Text
      },
      departure: {
        name: 'departure',
        title: 'Departure',
        type: EnhancedTableHeaderType.Text
      },
      destination: {
        name: 'destination',
        title: 'Destination',
        type: EnhancedTableHeaderType.Text
      },
      transportationVia: {
        name: 'transportationVia',
        title: 'Transportation Via',
        type: EnhancedTableHeaderType.Text
      },
      survey: {
        name: 'survey',
        title: 'Survey',
        type: EnhancedTableHeaderType.Text
      },
      sumPerShipment: {
        name: 'sumPerShipment',
        title: 'Sum Per Shipment',
        type: EnhancedTableHeaderType.RoundedCurrency
      },
      loading: {
        name: 'loading',
        title: 'Loading',
        type: EnhancedTableHeaderType.RoundedPercentage
      },
      sumInsured: {
        name: 'sumInsured',
        title: 'Sum Insured',
        type: EnhancedTableHeaderType.RoundedCurrency,
        currency: currencySymbol
      },
      netPremium: {
        name: 'netPremium',
        title: 'Net Premium',
        type: EnhancedTableHeaderType.RoundedCurrency,
        currency: currencySymbol
      },
      charges: {
        name: 'charges',
        title: 'Charges',
        type: EnhancedTableHeaderType.RoundedCurrency,
        currency: currencySymbol
      },
      tpaFees: {
        name: 'tpaFees',
        title: 'TPA Fees',
        type: EnhancedTableHeaderType.RoundedCurrency,
        currency: currencySymbol
      },
      grossPremium: {
        name: 'grossPremium',
        title: 'Gross Premium',
        type: EnhancedTableHeaderType.RoundedCurrency,
        currency: currencySymbol
      },
      proportionalStamp: {
        name: 'proportionalStamp',
        title: 'Prop Stamp',
        type: EnhancedTableHeaderType.RoundedCurrency,
        currency: currencySymbol
      },
      municipalityTax: {
        name: 'municipalityTax',
        title: 'Mun Tax',
        type: EnhancedTableHeaderType.RoundedCurrency,
        currency: currencySymbol
      },
      totalPremium: {
        name: 'totalPremium',
        title: 'Total Premium',
        type: EnhancedTableHeaderType.RoundedCurrency,
        currency: currencySymbol
      }
    };
  }

  if (!isFleetLine) return null;

  const headers: Record<string, EnhancedTableHeader> = {
    certificateNumber: {
      name: 'certificateNumber',
      title: 'Certificate Number',
      type: EnhancedTableHeaderType.Link,
      openUrlInNewTab: false,
      urlTemplate: '/production/policies/{policyId}'
    },
    subline: {
      name: 'subline',
      title: 'Subline',
      type: EnhancedTableHeaderType.Text
    },
    plan: {
      name: 'plan',
      title: 'Plan',
      type: EnhancedTableHeaderType.Text
    },
    makeModel: {
      name: 'makeModel',
      title: 'Make & Model',
      type: EnhancedTableHeaderType.Text
    },
    yearOfMake: {
      name: 'yearOfMake',
      title: 'Year Of Make',
      type: EnhancedTableHeaderType.Text
    },
    chassis: {
      name: 'chassis',
      title: 'Chassis',
      type: EnhancedTableHeaderType.Text
    },
    effectiveDate: {
      name: 'effectiveDate',
      title: 'Effective Date',
      type: EnhancedTableHeaderType.Date
    },
    carValue: {
      name: 'carValue',
      title: 'Car Value',
      type: EnhancedTableHeaderType.RoundedCurrency,
      currency: currencySymbol,
      hidden: !isAllRisk
    },
    agencyYears: {
      name: 'agencyYears',
      title: 'Agency Years',
      type: EnhancedTableHeaderType.Text,
      hidden: !isAllRisk
    },
    depreciationYears: {
      name: 'depreciationYears',
      title: 'Depreciation Years',
      type: EnhancedTableHeaderType.Text,
      hidden: !isAllRisk
    },
    sumInsured: {
      name: 'sumInsured',
      title: 'SumInsured',
      type: EnhancedTableHeaderType.RoundedCurrency,
      currency: currencySymbol
    },
    netPremium: {
      name: 'netPremium',
      title: 'Net Premium',
      type: EnhancedTableHeaderType.RoundedCurrency,
      currency: currencySymbol
    },
    charges: {
      name: 'charges',
      title: 'Charges',
      type: EnhancedTableHeaderType.RoundedCurrency,
      currency: currencySymbol
    },
    tpaFees: {
      name: 'tpaFees',
      title: 'TPA Fees',
      type: EnhancedTableHeaderType.RoundedCurrency,
      currency: currencySymbol
    },
    grossPremium: {
      name: 'grossPremium',
      title: 'Gross Premium',
      type: EnhancedTableHeaderType.RoundedCurrency,
      currency: currencySymbol
    },
    proportionalStamp: {
      name: 'proportionalStamp',
      title: 'Prop Stamp',
      type: EnhancedTableHeaderType.RoundedCurrency,
      currency: currencySymbol
    },
    municipalityTax: {
      name: 'municipalityTax',
      title: 'Mun Tax',
      type: EnhancedTableHeaderType.RoundedCurrency,
      currency: currencySymbol
    },
    totalPremium: {
      name: 'totalPremium',
      title: 'Total Premium',
      type: EnhancedTableHeaderType.RoundedCurrency,
      currency: currencySymbol
    }
  };

  return headers;
};
